import { Report } from 'organisms/CloudOpsReportAdmin/types';
import { CONTRACT_TYPE } from 'utils/constants/common';

type ReportEligibilityFields = Pick<
  Report,
  | 'invoice_number'
  | 'is_invoice_created'
  | 'month'
  | 'year'
  | 'error_state'
  | 'project'
  | 'invoice_creation_approval'
  | 'pm_approval'
  | 'contract_type'
  | 'total_amount'
  | 'adjustment_amount'
  | 'monthly_fee'
  | 'monthly_hourly_total_amount'
>;

const AUTOMATIC_INVOICE_CONTRACT_TYPES = [
  CONTRACT_TYPE.FIXED_PRICE,
  CONTRACT_TYPE.MONTHLY_RETAINER,
];

export const isBillingDataIncorrect = (
  report: Pick<ReportEligibilityFields, 'error_state'>
) =>
  Boolean(
    report.error_state?.some(
      ({ property }) => property !== 'pm_approved_time_log'
    )
  );

export const isCurrentMonthReport = (
  report: Pick<ReportEligibilityFields, 'month' | 'year'>,
  currentDate = new Date()
) =>
  report.month === currentDate.getMonth() + 1 &&
  report.year === currentDate.getFullYear();

const hasUnapprovedTimeLogs = (report: ReportEligibilityFields) => {
  if (
    report.error_state?.some(
      ({ property }) => property === 'pm_approved_time_log'
    )
  ) {
    return true;
  }

  const doesNotRequireTimeLogApproval = [
    CONTRACT_TYPE.FIXED_PRICE,
    CONTRACT_TYPE.MONTHLY_RETAINER,
  ].includes(report.contract_type);
  const approvalRequiredAtCostReport =
    report.project.invoice_creation_approval_required &&
    report.contract_type === CONTRACT_TYPE.TIME_AND_MATERIAL_AT_COST;

  return (
    !doesNotRequireTimeLogApproval &&
    !approvalRequiredAtCostReport &&
    report.pm_approval === false
  );
};

const hasAdjustments = (report: ReportEligibilityFields) =>
  Boolean(report.adjustment_amount);

const hasZeroTotal = (report: ReportEligibilityFields) =>
  report.total_amount === 0;

const requiresInvoiceCreationApproval = (report: ReportEligibilityFields) => {
  if (
    !report.project.invoice_creation_approval_required ||
    report.invoice_creation_approval
  ) {
    return false;
  }

  return (
    [
      CONTRACT_TYPE.FIXED_PRICE,
      CONTRACT_TYPE.MONTHLY_RETAINER,
      CONTRACT_TYPE.TIME_AND_MATERIAL_AT_COST,
    ].includes(report.contract_type) || report.pm_approval === true
  );
};

const isInvoiceCreationPending = (report: ReportEligibilityFields) => {
  const hasSourceAmount =
    Number(report.monthly_fee || 0) > 0 ||
    Number(report.monthly_hourly_total_amount || 0) > 0;

  if (
    !hasSourceAmount ||
    Boolean(report.invoice_number?.trim()) ||
    report.is_invoice_created ||
    Boolean(report.error_state?.length)
  ) {
    return false;
  }

  const automaticInvoice = AUTOMATIC_INVOICE_CONTRACT_TYPES.includes(
    report.contract_type
  );
  const approvalRequired =
    report.project.invoice_creation_approval_required === true;
  const invoiceApproved = report.invoice_creation_approval === true;
  const pmApproved = report.pm_approval === true;

  return (
    (automaticInvoice && !approvalRequired) ||
    (automaticInvoice && approvalRequired && invoiceApproved) ||
    (approvalRequired && pmApproved && invoiceApproved) ||
    (!approvalRequired && pmApproved)
  );
};

export const isReportBulkSelectable = (
  report: ReportEligibilityFields,
  currentDate = new Date()
) =>
  !report.invoice_number &&
  !report.is_invoice_created &&
  !isCurrentMonthReport(report, currentDate);

export const isReportBulkApprovable = (
  report: ReportEligibilityFields,
  currentDate = new Date()
) =>
  isReportBulkSelectable(report, currentDate) &&
  !hasZeroTotal(report) &&
  !isInvoiceCreationPending(report) &&
  !isBillingDataIncorrect(report) &&
  !hasUnapprovedTimeLogs(report) &&
  !hasAdjustments(report) &&
  requiresInvoiceCreationApproval(report);

export const isReportBulkUpdatable = (
  report: ReportEligibilityFields,
  currentDate = new Date()
) =>
  isReportBulkSelectable(report, currentDate) &&
  !isInvoiceCreationPending(report) &&
  (requiresInvoiceCreationApproval(report) ||
    hasZeroTotal(report) ||
    isBillingDataIncorrect(report) ||
    hasUnapprovedTimeLogs(report) ||
    hasAdjustments(report));
